'use client';

import { useEffect, useState } from 'react';
import type { ComponentType } from 'react';
import { usePathname } from 'next/navigation';

type RuntimeComponent = ComponentType<Record<string, never>>;
type GoogleAnalyticsComponent = ComponentType<{ ga4Id: string; nonce?: string }>;

function useRuntimeReady(timeoutMs = 3600) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return;

    const markReady = () => setReady(true);
    const timeoutId = window.setTimeout(markReady, timeoutMs);
    const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const;

    events.forEach((event) => {
      window.addEventListener(event, markReady, { passive: true, once: true });
    });

    return () => {
      window.clearTimeout(timeoutId);
      events.forEach((event) => {
        window.removeEventListener(event, markReady);
      });
    };
  }, [ready, timeoutMs]);

  return ready;
}

export function RootRuntimeLoaders({
  ga4Id,
  nonce,
}: {
  ga4Id: string;
  nonce?: string;
}) {
  const pathname = usePathname();
  const isHomepage = pathname === '/';
  const ready = useRuntimeReady(isHomepage ? 9000 : 15000);
  // GA4 gtag loads on a SHORTER gate than the heavy third-party pixels: a fast
  // (no-interaction) homepage bounce still produces a source-bearing
  // session_start instead of being dropped. Meta/TikTok/Ads-remarketing stay on
  // the longer `ready` gate so this does not regress LCP. (Tier-2 measurement.)
  const gaReady = useRuntimeReady(isHomepage ? 3000 : 12000);
  const [ConsentBanner, setConsentBanner] = useState<RuntimeComponent | null>(null);
  const [MetaPixel, setMetaPixel] = useState<RuntimeComponent | null>(null);
  const [TikTokPixel, setTikTokPixel] = useState<RuntimeComponent | null>(null);
  const [GoogleAdsRemarketing, setGoogleAdsRemarketing] = useState<RuntimeComponent | null>(null);
  const [FloatingVoiceGate, setFloatingVoiceGate] = useState<RuntimeComponent | null>(null);
  const [WebVitalsProvider, setWebVitalsProvider] = useState<RuntimeComponent | null>(null);
  const [GoogleAnalyticsIdleLoader, setGoogleAnalyticsIdleLoader] =
    useState<GoogleAnalyticsComponent | null>(null);

  useEffect(() => {
    let active = true;

    void import('@/components/consent/ConsentBanner').then((consent) => {
      if (active) setConsentBanner(() => consent.default);
    });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!ready) return;
    let active = true;

    void Promise.all([
      import('@/components/analytics/MetaPixel'),
      import('@/components/analytics/TikTokPixel'),
      import('@/components/analytics/GoogleAdsRemarketing'),
      import('@/components/voice/FloatingVoiceV1Gate'),
      import('@/components/providers/WebVitalsProvider'),
    ]).then(([meta, tiktok, gAds, floating, vitals]) => {
      if (!active) return;
      setMetaPixel(() => meta.default);
      setTikTokPixel(() => tiktok.default);
      setGoogleAdsRemarketing(() => gAds.default);
      setFloatingVoiceGate(() => floating.default);
      setWebVitalsProvider(() => vitals.WebVitalsProvider);
    });

    return () => {
      active = false;
    };
  }, [ready]);

  // GA4 gtag injection on its own shorter `gaReady` gate (see above).
  useEffect(() => {
    if (!gaReady) return;
    let active = true;

    void import('@/components/analytics/GoogleAnalyticsIdleLoader').then((ga) => {
      if (active) setGoogleAnalyticsIdleLoader(() => ga.GoogleAnalyticsIdleLoader);
    });

    return () => {
      active = false;
    };
  }, [gaReady]);

  return (
    <>
      {ConsentBanner && <ConsentBanner />}
      {MetaPixel && <MetaPixel />}
      {TikTokPixel && <TikTokPixel />}
      {GoogleAdsRemarketing && <GoogleAdsRemarketing />}
      {FloatingVoiceGate && <FloatingVoiceGate />}
      {WebVitalsProvider && <WebVitalsProvider />}
      {GoogleAnalyticsIdleLoader && <GoogleAnalyticsIdleLoader ga4Id={ga4Id} nonce={nonce} />}
    </>
  );
}
